/**
 * BindingsTable — Hotfix S5.0.1.
 *
 * Tabela de vínculos QR ↔ cliente IXC criados. Aparece embaixo do
 * wizard na ProvisioningPage. Permite:
 *   - Busca por qr_serial, device_id, customer_id ou pppoe_login
 *   - Pagination simples (limit fixo = 50)
 *   - Refresh manual
 *
 * NÃO permite editar/deletar daqui — apenas visualizar. Edição
 * acontece re-provisionando o mesmo qr_serial (UPSERT).
 */
import { useCallback, useEffect, useState } from "react";
import { listDeviceBindings } from "../lib/api.js";
import { formatRelative } from "../lib/format.js";
import { t } from "../i18n/index.js";
import ReplaceDeviceModal from "../components/ReplaceDeviceModal.jsx";

export default function BindingsTable({ refreshKey }) {
  const [bindings, setBindings] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // PROVISIONING-STOCK-QR-6-FRONTEND — binding em substituição (modal).
  const [replacing, setReplacing] = useState(null);

  // Debounce 300ms no campo de busca para evitar request por tecla.
  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(tid);
  }, [search]);

  // Carrega bindings quando search debounce muda ou refreshKey é bumpado
  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await listDeviceBindings({
        limit: 50,
        search: debouncedSearch || undefined,
      });
      setBindings(Array.isArray(data?.bindings) ? data.bindings : []);
      setCount(data?.count || 0);
    } catch (e) {
      setErr(e?.message || t("provisioning.bindingsTable.errLoad"));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <section className="card-pad space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
            {t("provisioning.bindingsTable.title")}
          </h2>
          <div className="text-[10px] text-noc-muted/70">
            {t("provisioning.bindingsTable.subtitle")}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("provisioning.bindingsTable.searchPlaceholder")}
            className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs w-72"
          />
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-xs px-2 py-1 rounded border border-noc-border text-noc-muted hover:text-noc-text disabled:opacity-40"
            title={t("provisioning.bindingsTable.refresh")}
          >
            🔄
          </button>
        </div>
      </div>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
          {err}
        </div>
      )}

      {loading && bindings.length === 0 ? (
        <div className="text-xs text-noc-muted py-4">{t("common.loading")}</div>
      ) : bindings.length === 0 ? (
        <div className="text-xs font-mono text-[12px] text-noc-faint py-4 text-center">
          {debouncedSearch
            ? t("provisioning.bindingsTable.noMatch")
            : t("provisioning.bindingsTable.empty")}
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-[11px]">
            <thead className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-noc-faint border-b border-noc-border">
              <tr>
                <Th>{t("provisioning.bindingsTable.colQr")}</Th>
                <Th>{t("provisioning.bindingsTable.colDevice")}</Th>
                <Th>{t("provisioning.bindingsTable.colCustomer")}</Th>
                <Th>{t("provisioning.bindingsTable.colContract")}</Th>
                <Th>{t("provisioning.bindingsTable.colLogin")}</Th>
                <Th>{t("provisioning.bindingsTable.colStatus")}</Th>
                <Th>{t("provisioning.bindingsTable.colTemplate")}</Th>
                <Th>{t("provisioning.bindingsTable.colCreatedAt")}</Th>
                <Th>Ações</Th>
              </tr>
            </thead>
            <tbody>
              {bindings.map((b) => (
                <tr
                  key={b.id}
                  className="border-b border-noc-border/40 hover:bg-noc-bg/30"
                >
                  <Td mono>{b.qr_serial}</Td>
                  <Td mono>
                    {b.device_id || (
                      <span className="text-noc-muted/60 italic">—</span>
                    )}
                  </Td>
                  <Td>
                    <CustomerCell binding={b} />
                  </Td>
                  <Td>
                    {b.contract_id ? (
                      <ContractCell binding={b} />
                    ) : (
                      <span className="text-noc-muted/60">—</span>
                    )}
                  </Td>
                  <Td mono>
                    {b.pppoe_login || (
                      <span className="text-noc-muted/60 italic">—</span>
                    )}
                  </Td>
                  <Td>
                    <BindingStatusBadge status={b.status} />
                  </Td>
                  <Td>
                    {b.template_name ? (
                      <span className="text-[11px] text-noc-accent">
                        {b.template_name}
                      </span>
                    ) : (
                      <span className="text-noc-muted/60 italic">—</span>
                    )}
                  </Td>
                  <Td>
                    <span title={b.created_at}>
                      {formatRelative(b.created_at)}
                    </span>
                  </Td>
                  <Td>
                    {/* PROVISIONING-STOCK-QR-6 — só faz sentido p/ binding já
                        vinculado (tem equipamento físico a substituir). */}
                    {b.status === "bound" ? (
                      <button
                        type="button"
                        onClick={() => setReplacing(b)}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-noc-warn/30 text-noc-warn hover:bg-noc-warn/10 whitespace-nowrap"
                        title="Substituir equipamento (troca de Sonoff)"
                      >
                        🔁 Substituir
                      </button>
                    ) : (
                      <span className="text-noc-muted/60">—</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10px] text-noc-muted/70 mt-2">
            {t("provisioning.bindingsTable.countSuffix", { n: count })}
          </div>
        </div>
      )}

      {replacing && (
        <ReplaceDeviceModal
          binding={replacing}
          onClose={() => setReplacing(null)}
          onReplaced={load}
        />
      )}
    </section>
  );
}

function Th({ children }) {
  return (
    <th className="px-2 py-1.5 text-left whitespace-nowrap font-semibold">
      {children}
    </th>
  );
}

function Td({ children, mono }) {
  return (
    <td
      className={`px-2 py-1.5 align-top ${mono ? "font-mono" : ""}`}
      style={{ wordBreak: "break-word" }}
    >
      {children}
    </td>
  );
}

/**
 * Mostra customer_id e, se houver snapshot raw_json.customer.razao,
 * o nome também (1 linha).
 */
function CustomerCell({ binding }) {
  const razao = binding.raw_json?.customer?.razao;
  return (
    <span>
      <span className="font-mono">#{binding.customer_id}</span>
      {razao && (
        <span className="text-noc-muted/80"> · {razao}</span>
      )}
    </span>
  );
}

/**
 * Mostra contract_id e nome do plano se houver no raw_json.
 */
function ContractCell({ binding }) {
  const plano = binding.raw_json?.contract?.contrato;
  return (
    <span>
      <span className="font-mono">#{binding.contract_id}</span>
      {plano && <span className="text-noc-muted/80"> · {plano}</span>}
    </span>
  );
}

/**
 * BindingStatusBadge — Sprint 5.0.3.
 * Pinta o status do binding (bound / pending_device).
 */
function BindingStatusBadge({ status }) {
  if (status === "bound") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-noc-ok/30 text-noc-ok bg-noc-ok/10">
        ● {t("provisioning.bindingsTable.statusBound")}
      </span>
    );
  }
  if (status === "pending_device") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-noc-warn/30 text-noc-warn bg-noc-warn/10">
        ⏳ {t("provisioning.bindingsTable.statusPending")}
      </span>
    );
  }
  return (
    <span className="text-[10px] text-noc-muted/60 italic">
      {status || "?"}
    </span>
  );
}
